import { View } from "@novorender/api";
import { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";

import { getFilePathFromObjectPath, hasDrawingsOrDocumentsSource, isLeafNode } from "utils/objectData";

import { PUBLIC_ID_PROPERTY_LOWERCASE } from "./constants";
import type { BoundsParts, ObjectDataAccess, PropertyQuery, SelectionBounds } from "./types";

/**
 * Whether a whole hierarchy — browsing it, searching it, and reading the objects in it —
 * can be answered with no network.
 *
 * Both halves, because the two answer different questions and a tree asks both: the index
 * serves children and name search ({@link ObjectDataAccess.isIndexAvailableOffline}),
 * while the objects those rows stand for come from the scene's own metadata
 * ({@link ObjectDataAccess.isDataAvailableOffline}). A backend with one and not the other
 * cannot carry a tree, so this is the condition a panel gates itself on rather than on
 * the connection.
 */
export function canBrowseHierarchyOffline(access: ObjectDataAccess | undefined): boolean {
    return Boolean(access?.isIndexAvailableOffline() && access.isDataAvailableOffline());
}

/**
 * One object's own bounds as a {@link SelectionBounds}, narrowed to what was asked for.
 */
export function objectBoundsAsSelection(bounds: ObjectData["bounds"], parts: BoundsParts): SelectionBounds | undefined {
    if (!bounds) {
        return undefined;
    }
    switch (parts) {
        case "sphere":
            return { sphere: bounds.sphere };
        case "box":
            return { box: bounds.box };
        case "both":
            return bounds;
    }
}

/**
 * Whether the object is a PDF/drawing document — for e.g. a canvas pick result, where
 * only the id is known and the path (and, for extension-less PDFs, the
 * "Procore/DocumentSource" property) has to be read. Extension-less DocumentSource
 * hits are documents only when they are leaves — the same `isLeafNode` gate as
 * `discoverPdfObjectIds` and `isPdfOrDrawingObject`. A federated file root reported
 * as Internal is not a document even with an empty subtree, so this fallback cannot
 * classify the Internal hash-path files discovery also misses (BM-13779).
 *
 * Reads `object.properties` from {@link ObjectDataAccess.getObject}, which is already
 * a full metadata load. That is the id-only analogue of `isPdfOrDrawingObject`'s
 * `loadMetaData()` on a listing row that may not carry properties yet.
 *
 * `false` when the id names nothing, when the read fails, and when no backend is hydrated
 * yet: none of them is a document, and a click cannot wait for one to become available.
 *
 * @param options.knownPdfObjectIds The scene's document ids — Procore's scene-wide
 * discovery (`utils/pdfObjectIds`), which admits a search hit only when its path satisfies
 * the same rule as below. Given one, it answers on its own and nothing is read: a hit is a
 * document, a miss is not. Pass it only once discovery has finished; while it is still
 * outstanding pass `undefined` so the object is read instead (see `useCanvasClickHandler`).
 * @see isPdfOrDrawingObject in `utils/objectData` for the variant that already holds a row.
 */
export async function isPdfOrDrawingObjectId(
    access: ObjectDataAccess | undefined,
    objectId: ObjectId,
    options?: { knownPdfObjectIds?: Pick<Set<ObjectId>, "has">; signal?: AbortSignal },
): Promise<boolean> {
    if (options?.knownPdfObjectIds) {
        return options.knownPdfObjectIds.has(objectId);
    }
    if (!access) {
        return false;
    }
    try {
        const object = await access.getObject(objectId, options?.signal);
        if (!object) {
            return false;
        }
        const filePath = getFilePathFromObjectPath(object.path);
        if (filePath) {
            return filePath.toLowerCase().endsWith(".pdf");
        }
        return isLeafNode(object) && hasDrawingsOrDocumentsSource(object.properties);
    } catch {
        return false;
    }
}

/**
 * The id a {@link PropertyQuery} asks for, when one id is the whole of what it asks.
 *
 * `id` is the one property both backends can answer without a search — the object DB
 * from the scene's metadata (or the object DB's own cache), the index from a direct
 * object read — so a caller resolving a single selected object should not pay for a
 * paged result set one row long. That caller is the common one:
 * `useLoadAncestorsForSelection` runs this per main object on every off-tree selection.
 *
 * `undefined` for everything else, including a negated or multi-valued term: those are
 * genuine searches, and a backend answers them as such.
 *
 * Named for what it returns rather than `isSingleIdPropertyQuery`, since the caller
 * needs the id and not just the verdict.
 */
export function singleIdPropertyQuery({ terms }: PropertyQuery): ObjectId | undefined {
    if (terms.length !== 1) {
        return undefined;
    }
    const [term] = terms;
    if (term.property.toLowerCase() !== "id" || term.negate || term.anyOf?.length !== 1) {
        return undefined;
    }
    const id = Number(term.anyOf[0]);
    return Number.isInteger(id) && id >= 0 ? id : undefined;
}

/**
 * One object read as the listing it stands in for — the row when there is one, nothing
 * when there is not, so a caller drains it exactly as it drains a search.
 */
export async function* oneObjectAsListing(
    read: Promise<ObjectData | undefined>,
): AsyncIterableIterator<HierarcicalObjectReference> {
    const object = await read;
    if (object) {
        yield object;
    }
}

export async function getObjectsByIdsOffline(view: View, idArr: ObjectId[], abortSignal: AbortSignal) {
    // pMap doesn't handle big arrays well, so use promise batches instead
    const { data } = view;
    if (!data) {
        return [];
    }
    const BATCH_SIZE = 16;
    const result = [] as HierarcicalObjectReference[];
    for (let i = 0; i < idArr.length; i += BATCH_SIZE) {
        const batch = idArr.slice(i, i + BATCH_SIZE);
        const objects = await Promise.all(batch.map((id) => data.getObjectMetaData(id)));
        if (abortSignal.aborted) {
            return [];
        }
        for (const obj of objects) {
            if (obj) {
                result.push(obj as unknown as HierarcicalObjectReference);
            }
        }
    }
    return result;
}

export function extractAnyOfPublicIdTermFromQuery(query: PropertyQuery): readonly string[] | undefined {
    if (query.terms.length !== 1) {
        return;
    }
    const term = query.terms[0];
    if (term.property.toLowerCase() !== PUBLIC_ID_PROPERTY_LOWERCASE || !term.anyOf || term.negate) {
        return;
    }
    return term.anyOf as string[];
}

export function extractAnyOfIdTermFromQuery(query: PropertyQuery): readonly number[] | undefined {
    if (query.terms.length !== 1) {
        return;
    }
    const term = query.terms[0];
    if (term.property.toLowerCase() !== "id" || !term.anyOf || term.negate) {
        return;
    }
    return term.anyOf as number[];
}
